import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Caption from 'LuxKit/Typography/Caption'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'

import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceRowLuxPlusPriceWithCaption from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceWithCaption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import getValueOffPercent from 'lib/offer/getValueOffPercent'

interface Props {
  selectedDay: App.CalendarDay
  offer: App.Offer
  isAnyFlightsActive?: boolean
  duration: number
  isFromPrice?: boolean
  displayMemberPrice: boolean
  canViewLuxPlusBenefits: boolean
}

function OfferCalenderDatePickerFooterPriceDetails(props: Props) {
  const { selectedDay, offer, isAnyFlightsActive, duration, isFromPrice, displayMemberPrice, canViewLuxPlusBenefits } = props
  const intl = useIntl()
  const canShowPrice = !(offer.bundledWithFlightsOnly && !selectedDay?.flightCacheActive)

  const [price, isLuxPlusPrice, value, discount] = useMemo((): [number, boolean, number, number] => {
    if (!selectedDay) return [0, false, 0, 0]

    const [pricing, memberPricing] = (isAnyFlightsActive && selectedDay.pricingWithFlights) ?
        [selectedDay.pricingWithFlights, selectedDay.memberPricingWithFlights] :
        [selectedDay.pricing, selectedDay.memberPricing]
    const isLuxPlusPrice = displayMemberPrice && !!memberPricing
    const { price } = isLuxPlusPrice ? memberPricing : pricing
    const value = canViewLuxPlusBenefits ? memberPricing?.value : pricing.value
    const effectiveValue = value ?? price

    return [
      price,
      isLuxPlusPrice,
      effectiveValue,
      offer.isDiscountPillHidden ? 0 : getValueOffPercent(effectiveValue, price),
    ]
  }, [selectedDay, isAnyFlightsActive, displayMemberPrice, canViewLuxPlusBenefits, offer.isDiscountPillHidden])

  const shouldShowDiscountPercent = (discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) && !offer.isDiscountPillHidden
  const durationCaption = intl.formatMessage({
    id: 'x3zgmlKvhm',
    defaultMessage: '{count, plural, one {# night} other {# nights}}',
    description: 'Duration caption on calendar date picker footer on the offer detail page',
  }, { count: duration })
  const durationFromCaption = isFromPrice
    ? intl.formatMessage({
        id: 'dXxqur+ria',
        defaultMessage: '{count, plural, one {# night} other {# nights}} from',
        description: 'Duration caption with from label on calendar date picker footer',
      }, { count: duration })
    : durationCaption

  const {
    saleUnit,
    total: displayPrice,
    value: displayValue,
  } = useOfferPrice({
    value,
    saleUnit: 'total',
    duration,
    total: price,
  })

  if (!canShowPrice) {
    return <Caption variant="large">
      {durationCaption}
    </Caption>
  }

  return <div>
    <LuxPlusPriceGuard
      showMemberPrice={isLuxPlusPrice}
      fallbackPrice={
        <PriceRowPriceWithCaption
          size="M"
          caption={durationFromCaption}
          price={displayPrice}
          saleUnit={`${saleUnit}${isAnyFlightsActive ? '*' : ''}`}
        />
      }
    >
      <PriceRowLuxPlusPriceWithCaption
        caption={durationFromCaption}
        memberPrice={displayPrice}
        saleUnit={`${saleUnit}${isAnyFlightsActive ? '*' : ''}`}
        size="M"
      />
    </LuxPlusPriceGuard>
    <PriceRowTaxesAndFees />
    {value > price && offer.shouldDisplayValue && displayValue && <PriceRowValueDiscountWithCaption
      size="M"
      originalValue={displayValue}
      discountPercentage={!isAnyFlightsActive && shouldShowDiscountPercent ? discount : undefined}
    />}
  </div>
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    displayMemberPrice: state.displayMemberPrice,
    canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(appState),
  }
}, OfferCalenderDatePickerFooterPriceDetails)
